import React, { useState, useEffect } from 'react';
import {
  Book, Info, Image, GraduationCap, Settings, CheckCircle,
  ChevronLeft, ChevronRight, Check, X
} from 'lucide-react';
import { MessageModal, LoadingSpinner } from '../../../Components/common';
import './CreateCourseTab.css';

// Import new tab components
import { BasicInfoTab } from './Components/CreateCourse/BasicInfoTab';
import { CourseDetailsTab } from './Components/CreateCourse/CourseDetailsTab';
import { MediaTab } from './Components/CreateCourse/MediaTab';
import { CourseContentTab } from './Components/CreateCourse/CourseContentTab';
import SectionsLessonsTab from './Components/CreateCourse/SectionsLessonsTab';
import OfferingsSessionsTab from './Components/CreateCourse/OfferingsSessionsTab';
import { SettingsTab } from './Components/CreateCourse/SettingsTab';
import { ReviewTab } from './Components/CreateCourse/ReviewTab';
import type { FAQ } from './Components/CreateCourse/CourseDetailsTab';

// Import existing components for course content
import type { SelectedModule, Offering, Session } from './Components/CreateCourse/CourseContentTab';

// Import services
import {
  createCourse,
  updateCourse,
  getCourseDetails,
  getCategories,
  getSubCategories,
  getLanguages,
  getInstructors,
  addSection,
  addLesson,
  addCategory,
  addSubCategory,
  type Category,
  type SubCategory,
  type Language,
  type Instructor,
} from '../../../services/AdminService/AdminCourseManagementService';
import {
  uploadCourseThumbnail,
  uploadLessonMp4,
  uploadLessonDocument,
  uploadLessonScorm,
} from '../../../services/UploadService';
import UserService, { type UserDetails } from '../../../services/UserService';

// Content Library Data
interface ContentLibraryItem {
  id: number;
  title: string;
  type: string;
  duration: string;
}

const contentLibrary: ContentLibraryItem[] = [
  { id: 1, title: 'Introduction to Leadership Principles', type: 'Video', duration: '45 min' },
  { id: 2, title: 'Building High-Performance Teams', type: 'Interactive', duration: '60 min' },
  { id: 3, title: 'Strategic Decision Making', type: 'SCORM', duration: '90 min' },
  { id: 4, title: 'Data Analysis with Python', type: 'Video', duration: '120 min' },
  { id: 5, title: 'Effective Communication Skills', type: 'Interactive', duration: '50 min' },
  { id: 6, title: 'Project Management Fundamentals', type: 'SCORM', duration: '80 min' },
];

// Tab configuration
interface Tab {
  id: string;
  label: string;
  icon: typeof Book;
  step: number;
  description: string;
}

const tabs: Tab[] = [
  { id: 'basic', label: 'Basic Info', icon: Book, step: 1, description: 'Course fundamentals' },
  { id: 'details', label: 'Course Details', icon: Info, step: 2, description: 'Additional information' },
  { id: 'media', label: 'Media & Assets', icon: Image, step: 3, description: 'Visual content' },
  { id: 'sections', label: 'Sections & Lessons', icon: GraduationCap, step: 4, description: 'Course structure' },
  { id: 'offerings', label: 'Offerings & Sessions', icon: GraduationCap, step: 5, description: 'Training schedules (optional)' },
  { id: 'settings', label: 'Settings', icon: Settings, step: 6, description: 'Rules & enrollment' },
  { id: 'review', label: 'Review & Publish', icon: CheckCircle, step: 7, description: 'Final review' },
];

interface CreateCourseTabProps {
  onBackToList?: () => void;
}

const CreateCourseTab: React.FC<CreateCourseTabProps> = ({ onBackToList }) => {
  // Tab navigation state
  const [activeTab, setActiveTab] = useState<string>('basic');
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [completedTabs, setCompletedTabs] = useState<string[]>([]);

  // Current user state
  const [currentUser, setCurrentUser] = useState<UserDetails | null>(null);
  const [instructorName, setInstructorName] = useState('Loading...');

  // Metadata state
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [selectedInstructorId, setSelectedInstructorId] = useState<string>('');
  const [loadingMetadata, setLoadingMetadata] = useState(true);

  // Basic Info state
  const [courseType, setCourseType] = useState<'web' | 'ilt' | 'blended'>('ilt');
  const [courseTitle, setCourseTitle] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [language, setLanguage] = useState('');
  const [level, setLevel] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [courseDuration, setCourseDuration] = useState('');
  const [status, setStatus] = useState<'pending' | 'active' | 'inactive'>('pending');

  // Course Details state
  const [tags, setTags] = useState<string[]>([]);
  const [requirements, setRequirements] = useState<string[]>(['']);
  const [outcomes, setOutcomes] = useState<string[]>(['']);
  const [faqs, setFAQs] = useState<FAQ[]>([{ question: '', answer: '' }]);

  // Media state
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);

  // Course Content state - Modules
  const [selectedModules, setSelectedModules] = useState<SelectedModule[]>([]);
  const [showModulesModal, setShowModulesModal] = useState(false);
  const [tempSelectedModules, setTempSelectedModules] = useState<number[]>([]);

  // Course Content state - ILT Offerings
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [showOfferingForm, setShowOfferingForm] = useState(false);
  const [expandedOffering, setExpandedOffering] = useState<number | null>(null);
  const [offeringName, setOfferingName] = useState('');
  const [offeringLocation, setOfferingLocation] = useState('');
  const [offeringStartDate, setOfferingStartDate] = useState('');
  const [offeringEndDate, setOfferingEndDate] = useState('');
  const [offeringTimezone, setOfferingTimezone] = useState('IST');
  const [offeringSeats, setOfferingSeats] = useState('');
  const [enableWaitlist, setEnableWaitlist] = useState(false);

  // Course Content state - ILT Sessions
  const [sessions, setSessions] = useState<Session[]>([]);
  const [showSessionForm, setShowSessionForm] = useState(false);
  const [sessionDate, setSessionDate] = useState('');
  const [sessionStartTime, setSessionStartTime] = useState('');
  const [sessionEndTime, setSessionEndTime] = useState('');
  const [sessionInstructor, setSessionInstructor] = useState('');
  const [sessionDeliveryMethod, setSessionDeliveryMethod] = useState('Virtual');
  const [sessionLocationRoom, setSessionLocationRoom] = useState('');
  const [sessionVirtualLink, setSessionVirtualLink] = useState('');

  // Settings state
  const [visibilityType, setVisibilityType] = useState<'public' | 'restricted'>('public');
  const [requiredModules, setRequiredModules] = useState(false);
  const [minimumScore, setMinimumScore] = useState('70');
  const [sequentialLearning, setSequentialLearning] = useState(false);
  const [managerApproval, setManagerApproval] = useState(false);
  const [enrollmentType, setEnrollmentType] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [sendReminders, setSendReminders] = useState(false);

  // Loading and error states
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Course ID state (set after creation for sections/lessons/offerings tabs)
  const [createdCourseId, setCreatedCourseId] = useState<number | undefined>(undefined);

  // Add Category/Subcategory modal states
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showAddSubcategoryModal, setShowAddSubcategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [addingSubcategory, setAddingSubcategory] = useState(false);

  // Message Modal state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [modalType, setModalType] = useState<'success' | 'error'>('success');
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');

  // Helper to show message modal
  const showModal = (type: 'success' | 'error', title: string, message: string) => {
    setModalType(type);
    setModalTitle(title);
    setModalMessage(message);
    setShowMessageModal(true);
  };

  // Handle message modal close
  const handleMessageModalClose = () => {
    setShowMessageModal(false);
    // If it was a success modal for course creation/publishing, reset form and go back
    if (modalType === 'success' && modalTitle === 'Course Published Successfully!') {
      resetForm();
    }
  };

  // Load current user and metadata on mount
  useEffect(() => {
    const loadInitialData = async () => {
      setLoadingMetadata(true);
      setError(null);
      try {
        // Load current user profile
        const userProfile = await UserService.getOwnProfile();
        if (!userProfile || !userProfile.uuid) {
          throw new Error('Failed to load user profile. Please ensure you are logged in.');
        }
        setCurrentUser(userProfile);

        // Set instructor name from current user
        const fullName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || 'Current User';
        setInstructorName(fullName);

        // Load metadata
        const [categoriesRes, subcategoriesRes, languagesRes, instructorsRes] = await Promise.all([
          getCategories(),
          getSubCategories(),
          getLanguages(),
          getInstructors(),
        ]);

        if (categoriesRes.success && categoriesRes.data) {
          console.log('Categories loaded:', categoriesRes.data);
          setCategories(categoriesRes.data);
        } else {
          console.warn('Failed to load categories:', categoriesRes);
        }

        if (subcategoriesRes.success && subcategoriesRes.data) {
          console.log('Subcategories loaded:', subcategoriesRes.data);
          setSubcategories(subcategoriesRes.data);
        } else {
          console.warn('Failed to load subcategories:', subcategoriesRes);
        }

        if (languagesRes.success && languagesRes.data) {
          console.log('Languages loaded:', languagesRes.data);
          setLanguages(languagesRes.data);
        } else {
          console.warn('Failed to load languages:', languagesRes);
        }

        if (instructorsRes.success && instructorsRes.data) {
          console.log('Instructors loaded:', instructorsRes.data);
          setInstructors(instructorsRes.data);
          // Auto-select current user if they are in the instructors list
          const currentUserInstructor = instructorsRes.data.find(
            (instructor) => instructor.userId === userProfile.uuid
          );
          if (currentUserInstructor) {
            setSelectedInstructorId(currentUserInstructor.userId);
          }
        } else {
          console.warn('Failed to load instructors:', instructorsRes);
        }

        // Check if critical metadata failed to load
        if (!categoriesRes.success || !languagesRes.success) {
          setError('Failed to load course metadata. Some options may not be available.');
        }
      } catch (err: any) {
        console.error('Failed to load initial data:', err);
        const errorMessage = err.message || 'Failed to load course data. Please refresh the page.';
        setError(errorMessage);
        setInstructorName('Unable to load user');
      } finally {
        setLoadingMetadata(false);
      }
    };

    loadInitialData();
  }, []);

  // No need to load course data for create mode

  // Helper functions for course details
  const handleAddTag = (tag: string) => {
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
    }
  };

  const handleRemoveTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const handleAddRequirement = () => {
    setRequirements([...requirements, '']);
  };

  const handleRemoveRequirement = (index: number) => {
    setRequirements(requirements.filter((_, i) => i !== index));
  };

  const handleUpdateRequirement = (index: number, value: string) => {
    setRequirements(requirements.map((item, i) => (i === index ? value : item)));
  };

  const handleAddOutcome = () => {
    setOutcomes([...outcomes, '']);
  };

  const handleRemoveOutcome = (index: number) => {
    setOutcomes(outcomes.filter((_, i) => i !== index));
  };

  const handleUpdateOutcome = (index: number, value: string) => {
    setOutcomes(outcomes.map((item, i) => (i === index ? value : item)));
  };

  const handleAddFAQ = () => {
    setFAQs([...faqs, { question: '', answer: '' }]);
  };

  const handleRemoveFAQ = (index: number) => {
    setFAQs(faqs.filter((_, i) => i !== index));
  };

  const handleUpdateFAQ = (index: number, faq: FAQ) => {
    setFAQs(faqs.map((item, i) => (i === index ? faq : item)));
  };

  // Module handlers
  const handleOpenModulesModal = () => {
    setTempSelectedModules(selectedModules.map(m => m.id));
    setShowModulesModal(true);
  };

  const handleToggleModuleSelection = (moduleId: number) => {
    if (tempSelectedModules.includes(moduleId)) {
      setTempSelectedModules(tempSelectedModules.filter(id => id !== moduleId));
    } else {
      setTempSelectedModules([...tempSelectedModules, moduleId]);
    }
  };

  const handleSaveModules = () => {
    const newSelectedModules = contentLibrary.filter(item => tempSelectedModules.includes(item.id));
    setSelectedModules(newSelectedModules as SelectedModule[]);
    setShowModulesModal(false);
  };

  const handleRemoveModule = (moduleId: number) => {
    setSelectedModules(selectedModules.filter(m => m.id !== moduleId));
  };

  const handleAddModule = (module: SelectedModule) => {
    setSelectedModules([...selectedModules, module]);
  };

  // Offering handlers
  const handleAddOffering = () => {
    if (!offeringName || !offeringLocation) {
      showModal('error', 'Required Fields', 'Please fill in all required fields for the offering.');
      return;
    }

    const newOffering: Offering = {
      id: offerings.length + 1,
      name: offeringName,
      location: offeringLocation,
      startDate: offeringStartDate,
      endDate: offeringEndDate,
      timezone: offeringTimezone,
      totalSeats: parseInt(offeringSeats) || 0,
      enableWaitlist,
    };

    setOfferings([...offerings, newOffering]);
    setShowOfferingForm(false);
    // Reset form
    setOfferingName('');
    setOfferingLocation('');
    setOfferingStartDate('');
    setOfferingEndDate('');
    setOfferingTimezone('IST');
    setOfferingSeats('');
    setEnableWaitlist(false);
  };

  const handleDeleteOffering = (id: number) => {
    setOfferings(offerings.filter(o => o.id !== id));
  };

  // Session handlers
  const handleAddSession = () => {
    if (!sessionDate || !sessionStartTime || !sessionEndTime) {
      showModal('error', 'Required Fields', 'Please fill in session date and times.');
      return;
    }

    const newSession: Session = {
      id: sessions.length + 1,
      name: `Session ${sessions.length + 1}`,
      date: sessionDate,
      startTime: sessionStartTime,
      endTime: sessionEndTime,
      instructor: sessionInstructor,
      deliveryMethod: sessionDeliveryMethod,
      locationRoom: sessionLocationRoom,
      virtualMeetingLink: sessionVirtualLink,
    };

    setSessions([...sessions, newSession]);

    // Reset session form
    setShowSessionForm(false);
    setSessionDate('');
    setSessionStartTime('');
    setSessionEndTime('');
    setSessionInstructor('');
    setSessionDeliveryMethod('Virtual');
    setSessionLocationRoom('');
    setSessionVirtualLink('');
  };

  const handleDeleteSession = (sessionId: number) => {
    setSessions(sessions.filter(session => session.id !== sessionId));
  };

  // Add Category handler
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setError('Category name is required');
      return;
    }

    setAddingCategory(true);
    setError(null);

    try {
      const result = await addCategory(newCategoryName.trim());

      if (result.success && result.data) {
        const newCategoryId = result.data.id;

        // Refetch categories list to get the latest data
        const categoriesRes = await getCategories();
        if (categoriesRes.success && categoriesRes.data) {
          setCategories(categoriesRes.data);
        }

        // Select the newly added category
        setCategory(newCategoryId.toString());

        // Reset subcategory since we changed categories
        setSubcategory('');

        // Close modal and reset form
        setShowAddCategoryModal(false);
        setNewCategoryName('');

        // Show success modal
        showModal('success', 'Success', 'Category added successfully!');
      } else {
        throw new Error(result.error?.message || 'Failed to add category');
      }
    } catch (err: any) {
      console.error('Error adding category:', err);
      showModal('error', 'Error', err.message || 'Failed to add category');
    } finally {
      setAddingCategory(false);
    }
  };

  // Add Subcategory handler
  const handleAddSubcategory = async () => {
    if (!category) {
      setError('Please select a category first');
      return;
    }

    if (!newSubcategoryName.trim()) {
      setError('Subcategory name is required');
      return;
    }

    setAddingSubcategory(true);
    setError(null);

    try {
      const result = await addSubCategory(parseInt(category), newSubcategoryName.trim());

      if (result.success && result.data) {
        const newSubcategoryId = result.data.id;

        // Refetch subcategories list to get the latest data
        const subcategoriesRes = await getSubCategories();
        if (subcategoriesRes.success && subcategoriesRes.data) {
          setSubcategories(subcategoriesRes.data);
        }

        // Select the newly added subcategory
        setSubcategory(newSubcategoryId.toString());

        // Close modal and reset form
        setShowAddSubcategoryModal(false);
        setNewSubcategoryName('');

        // Show success modal
        showModal('success', 'Success', 'Subcategory added successfully!');
      } else {
        throw new Error(result.error?.message || 'Failed to add subcategory');
      }
    } catch (err: any) {
      console.error('Error adding subcategory:', err);
      showModal('error', 'Error', err.message || 'Failed to add subcategory');
    } finally {
      setAddingSubcategory(false);
    }
  };

  // Validation
  const validateCurrentTab = () => {
    const errors: Record<string, string> = {};

    switch (activeTab) {
      case 'basic':
        if (!courseTitle.trim()) errors.courseTitle = 'Course title is required';
        if (!category) errors.category = 'Category is required';
        if (!subcategory) errors.subcategory = 'Subcategory is required';
        if (!language) errors.language = 'Language is required';
        break;
      case 'details':
        // Optional: Add validation if needed
        break;
      case 'media':
        // Optional: Thumbnail can be skipped
        break;
      case 'content':
        // Optional: Can be added later
        break;
      case 'settings':
        // All optional
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Create draft course after Basic Info
  const handleCreateDraftCourse = async (): Promise<number | null> => {
    if (createdCourseId) {
      // Course already created, return existing ID
      return createdCourseId;
    }

    setIsCreating(true);
    setError(null);

    try {
      // Ensure we have current user
      if (!currentUser) {
        throw new Error('User information not loaded. Please refresh the page.');
      }

      // Prepare minimal course payload for draft
      const selectedInstructor = instructors.find(i => i.userId === selectedInstructorId);
      const coursePayload = {
        title: courseTitle,
        instructor: selectedInstructor?.fullName || instructorName,
        creator_id: selectedInstructorId || '',
        shortDescription: shortDescription || undefined,
        description: description || undefined,
        languageId: parseInt(language),
        categoryId: parseInt(category),
        subCategoryId: parseInt(subcategory),
        level: level,
        courseDuration: courseDuration || undefined,
        metaKeywords: [],
        outcomes: [],
        requirements: [],
        faqs: [],
        lessons: [],
        // Batch Availability was removed from the UI. The backend always reassigns
        // batches on create, so send availableToAllBatches to avoid recording the
        // course as restricted to zero batches.
        batchIds: [],
        availableToAllBatches: true,
      };

      console.log('Creating draft course with payload:', coursePayload);

      // Create course as draft
      const result = await createCourse(coursePayload);

      if (!result.success || !result.data) {
        throw new Error(result.error?.message || 'Failed to create draft course');
      }

      const courseId = result.data.courseId || result.data.id;
      console.log('Draft course created successfully with ID:', courseId);

      // Save the course ID for sections/lessons/offerings tabs
      setCreatedCourseId(courseId);
      setSuccess('Course saved as draft. You can now add sections and lessons.');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);

      return courseId;

    } catch (err: any) {
      console.error('Error creating draft course:', err);
      setError(err.message || 'Failed to create draft course');
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  // Update Course Details (tags, requirements, outcomes, FAQs)
  const handleUpdateCourseDetails = async (): Promise<boolean> => {
    if (!createdCourseId) {
      setError('Course must be created first before saving details');
      return false;
    }

    setIsCreating(true);
    setError(null);

    try {
      const selectedInstructor = instructors.find(i => i.userId === selectedInstructorId);
      const coursePayload = {
        title: courseTitle,
        instructor: selectedInstructor?.fullName || instructorName,
        creator_id: selectedInstructorId || '',
        shortDescription: shortDescription || undefined,
        description: description || undefined,
        languageId: parseInt(language),
        categoryId: parseInt(category),
        subCategoryId: parseInt(subcategory),
        level: level,
        courseDuration: courseDuration || undefined,
        metaKeywords: tags,
        outcomes: outcomes.filter(o => o.trim()),
        requirements: requirements.filter(r => r.trim()),
        faqs: faqs.filter(f => f.question.trim() && f.answer.trim()),
        lessons: [],
        // Batch Availability was removed from the UI. The backend always reassigns
        // batches on create, so send availableToAllBatches to avoid recording the
        // course as restricted to zero batches.
        batchIds: [],
        availableToAllBatches: true,
      };

      console.log('Updating course details for course ID:', createdCourseId);
      const updateResult = await updateCourse(createdCourseId, coursePayload);

      if (!updateResult.success) {
        throw new Error(updateResult.error?.message || 'Failed to update course details');
      }

      console.log('Course details updated successfully');
      setSuccess('Course details saved!');
      setTimeout(() => setSuccess(null), 2000);
      return true;

    } catch (err: any) {
      console.error('Error updating course details:', err);
      setError(err.message || 'Failed to update course details');
      return false;
    } finally {
      setIsCreating(false);
    }
  };

  // Update Media (thumbnail)
  const handleUpdateMedia = async (): Promise<boolean> => {
    if (!createdCourseId) {
      setError('Course must be created first before uploading media');
      return false;
    }

    // If no thumbnail, just skip (it's optional)
    if (!thumbnailFile) {
      console.log('No thumbnail to upload, skipping');
      return true;
    }

    setIsCreating(true);
    setError(null);

    try {
      console.log('Uploading course thumbnail for course ID:', createdCourseId);
      const uploadResult = await uploadCourseThumbnail(thumbnailFile, createdCourseId);

      if (!uploadResult.success) {
        throw new Error(uploadResult.error?.message || 'Failed to upload thumbnail');
      }

      console.log('Thumbnail uploaded successfully');
      setSuccess('Media uploaded successfully!');
      setTimeout(() => setSuccess(null), 2000);
      return true;

    } catch (err: any) {
      console.error('Error uploading media:', err);
      setError(err.message || 'Failed to upload media');
      return false;
    } finally {
      setIsCreating(false);
    }
  };

  // Navigation
  const handleNext = async () => {
    if (!validateCurrentTab()) {
      return;
    }

    // Progressive saving based on current tab
    if (activeTab === 'basic' && !createdCourseId) {
      // Create draft course from Basic Info
      const newCourseId = await handleCreateDraftCourse();
      if (!newCourseId) {
        return;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    } else if (activeTab === 'details' && createdCourseId) {
      // Save Course Details
      const success = await handleUpdateCourseDetails();
      if (!success) {
        return;
      }
    } else if (activeTab === 'media' && createdCourseId) {
      // Save Media
      const success = await handleUpdateMedia();
      if (!success) {
        return;
      }
    }

    // Mark current tab as completed
    if (!completedTabs.includes(activeTab)) {
      setCompletedTabs([...completedTabs, activeTab]);
    }

    // Move to next tab
    const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1].id);
      setCurrentStep(currentIndex + 2);
    }
  };

  const handlePrevious = () => {
    const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1].id);
      setCurrentStep(currentIndex);
    }
  };

  // Create/Publish Course handler (status = inactive)
  const handleCreateCourse = async () => {
    // Validation
    if (!courseTitle.trim()) {
      setError('Course title is required');
      return;
    }
    if (!category) {
      setError('Category is required');
      return;
    }
    if (!subcategory) {
      setError('Subcategory is required');
      return;
    }
    if (!language) {
      setError('Language is required');
      return;
    }

    const startTime = Date.now(); // Track start time for minimum loading duration
    setIsCreating(true);
    setError(null);
    setSuccess(null);

    try {
      // Ensure we have current user
      if (!currentUser) {
        throw new Error('User information not loaded. Please refresh the page.');
      }

      // Prepare course payload
      const selectedInstructor = instructors.find(i => i.userId === selectedInstructorId);
      const coursePayload = {
        title: courseTitle,
        instructor: selectedInstructor?.fullName || instructorName,
        creator_id: selectedInstructorId || '',
        shortDescription: shortDescription || undefined,
        description: description || undefined,
        languageId: parseInt(language),
        categoryId: parseInt(category),
        subCategoryId: parseInt(subcategory),
        level: level,
        courseDuration: courseDuration || undefined,
        metaKeywords: tags,
        outcomes: outcomes.filter(o => o.trim()),
        requirements: requirements.filter(r => r.trim()),
        faqs: faqs.filter(f => f.question.trim() && f.answer.trim()),
        lessons: [],
        // Batch Availability was removed from the UI. The backend always reassigns
        // batches on create, so send availableToAllBatches to avoid recording the
        // course as restricted to zero batches.
        batchIds: [],
        availableToAllBatches: true,
      };

      let courseId = createdCourseId;

      // If course already exists (draft), update it
      if (createdCourseId) {
        console.log('Updating existing course with ID:', createdCourseId);
        console.log('Update payload:', coursePayload);

        const updateResult = await updateCourse(createdCourseId, coursePayload);

        if (!updateResult.success) {
          throw new Error(updateResult.error?.message || 'Failed to update course');
        }

        console.log('Course updated successfully');
      } else {
        // Create new course
        console.log('Creating new course with payload:', coursePayload);
        console.log('Current user UUID:', currentUser.uuid);

        const result = await createCourse(coursePayload);

        if (!result.success || !result.data) {
          throw new Error(result.error?.message || 'Failed to create course');
        }

        courseId = result.data.courseId || result.data.id;
        console.log('Course created successfully with ID:', courseId);

        // Save the course ID
        setCreatedCourseId(courseId);
      }

      // Upload thumbnail if provided
      if (thumbnailFile && courseId) {
        console.log('Uploading thumbnail...');
        const uploadResult = await uploadCourseThumbnail(thumbnailFile, courseId);
        if (!uploadResult.success) {
          console.error('Thumbnail upload failed:', uploadResult.error);
        }
      }

      // Create sections and upload module files
      if (selectedModules.length > 0 && courseId) {
        console.log('Creating sections and lessons...');

        const sectionResult = await addSection(courseId, 'Course Content');

        if (sectionResult.success && sectionResult.data) {
          const sectionId = sectionResult.data.sectionId;

          // Add lessons and upload files
          for (const module of selectedModules) {
            if (module.contentType && module.file) {
              const lessonData = {
                title: module.title,
                section: 'Course Content',
                sectionId: sectionId,
                lessonType: 'Content-Based' as const,
                contentType: module.contentType,
                lessonDuration: module.duration,
              };

              const lessonResult = await addLesson(courseId, lessonData);

              if (lessonResult.success && lessonResult.data) {
                const lessonId = lessonResult.data.id;

                // Upload the file based on content type
                if (module.contentType === 'mp4' && module.file) {
                  const uploadRes = await uploadLessonMp4(module.file, lessonId);
                  if (!uploadRes.success) {
                    console.error('Video upload failed:', uploadRes.error);
                  }
                } else if (module.contentType === 'document' && module.file) {
                  const uploadRes = await uploadLessonDocument(module.file, lessonId);
                  if (!uploadRes.success) {
                    console.error('Document upload failed:', uploadRes.error);
                  }
                } else if (module.contentType === 'scorm' && module.file) {
                  const uploadRes = await uploadLessonScorm(module.file, lessonId);
                  if (!uploadRes.success) {
                    console.error('SCORM upload failed:', uploadRes.error);
                  }
                }
              }
            }
          }
        }
      }

      // Show success modal
      const successMsg = createdCourseId
        ? 'Course has been successfully updated and published!'
        : 'Course has been successfully created and published!';

      showModal('success', 'Course Published Successfully!', successMsg);

    } catch (err: any) {
      console.error('Error creating course:', err);
      showModal('error', 'Error', err.message || 'Failed to create course');
    } finally {
      // Ensure minimum 1 second loading time
      const elapsed = Date.now() - startTime;
      if (elapsed < 1000) {
        await new Promise(resolve => setTimeout(resolve, 1000 - elapsed));
      }
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    setCourseTitle('');
    setShortDescription('');
    setDescription('');
    setCategory('');
    setSubcategory('');
    setLanguage('');
    setLevel('beginner');
    setCourseDuration('');
    setStatus('pending');
    setThumbnailFile(null);
    setTags([]);
    setRequirements(['']);
    setOutcomes(['']);
    setFAQs([{ question: '', answer: '' }]);
    setSelectedModules([]);
    setOfferings([]);
    setSessions([]);
    setError(null);
    setSuccess(null);
    setActiveTab('basic');
    setCurrentStep(1);
    setCompletedTabs([]);
    setCreatedCourseId(undefined); // Reset course ID for new course

    // Go back to list if callback is provided
    if (onBackToList) {
      onBackToList();
    }
  };

  // Get display names for review tab
  const getCategoryName = () => categories.find(c => c.id === parseInt(category))?.name || '';
  const getSubcategoryName = () => subcategories.find(s => s.id === parseInt(subcategory))?.name || '';
  const getLanguageName = () => languages.find(l => l.id === parseInt(language))?.name || '';

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <BasicInfoTab
            courseType={courseType}
            courseTitle={courseTitle}
            instructorName={instructorName}
            shortDescription={shortDescription}
            description={description}
            category={category}
            subcategory={subcategory}
            language={language}
            level={level}
            courseDuration={courseDuration}
            categories={categories}
            subcategories={subcategories}
            languages={languages}
            instructors={instructors}
            selectedInstructorId={selectedInstructorId}
            onInstructorChange={setSelectedInstructorId}
            loadingMetadata={loadingMetadata}
            onCourseTypeChange={setCourseType}
            onCourseTitleChange={setCourseTitle}
            onShortDescriptionChange={setShortDescription}
            onDescriptionChange={setDescription}
            onCategoryChange={setCategory}
            onSubcategoryChange={setSubcategory}
            onLanguageChange={setLanguage}
            onLevelChange={setLevel}
            onCourseDurationChange={setCourseDuration}
            validationErrors={validationErrors}
            onAddCategoryClick={() => setShowAddCategoryModal(true)}
            onAddSubcategoryClick={() => setShowAddSubcategoryModal(true)}
          />
        );

      case 'details':
        return (
          <CourseDetailsTab
            tags={tags}
            requirements={requirements}
            outcomes={outcomes}
            faqs={faqs}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
            onAddRequirement={handleAddRequirement}
            onRemoveRequirement={handleRemoveRequirement}
            onUpdateRequirement={handleUpdateRequirement}
            onAddOutcome={handleAddOutcome}
            onRemoveOutcome={handleRemoveOutcome}
            onUpdateOutcome={handleUpdateOutcome}
            onAddFAQ={handleAddFAQ}
            onRemoveFAQ={handleRemoveFAQ}
            onUpdateFAQ={handleUpdateFAQ}
            validationErrors={validationErrors}
          />
        );

      case 'media':
        return (
          <MediaTab
            thumbnailFile={thumbnailFile}
            onThumbnailUpload={setThumbnailFile}
            onThumbnailRemove={() => setThumbnailFile(null)}
            validationErrors={validationErrors}
          />
        );

      case 'content':
        return (
          <CourseContentTab
            courseType={courseType}
            selectedModules={selectedModules}
            onOpenModulesModal={handleOpenModulesModal}
            onRemoveModule={handleRemoveModule}
            onAddModule={handleAddModule}
            offerings={offerings}
            showOfferingForm={showOfferingForm}
            expandedOffering={expandedOffering}
            offeringName={offeringName}
            offeringLocation={offeringLocation}
            offeringStartDate={offeringStartDate}
            offeringEndDate={offeringEndDate}
            offeringTimezone={offeringTimezone}
            offeringSeats={offeringSeats}
            enableWaitlist={enableWaitlist}
            onShowOfferingFormToggle={() => setShowOfferingForm(!showOfferingForm)}
            onExpandedOfferingToggle={setExpandedOffering}
            onOfferingNameChange={setOfferingName}
            onOfferingLocationChange={setOfferingLocation}
            onOfferingStartDateChange={setOfferingStartDate}
            onOfferingEndDateChange={setOfferingEndDate}
            onOfferingTimezoneChange={setOfferingTimezone}
            onOfferingSeatsChange={setOfferingSeats}
            onEnableWaitlistChange={setEnableWaitlist}
            onAddOffering={handleAddOffering}
            onDeleteOffering={handleDeleteOffering}
            sessions={sessions}
            showSessionForm={showSessionForm}
            sessionDate={sessionDate}
            sessionStartTime={sessionStartTime}
            sessionEndTime={sessionEndTime}
            sessionInstructor={sessionInstructor}
            sessionDeliveryMethod={sessionDeliveryMethod}
            sessionLocationRoom={sessionLocationRoom}
            sessionVirtualLink={sessionVirtualLink}
            onShowSessionFormToggle={() => setShowSessionForm(!showSessionForm)}
            onSessionDateChange={setSessionDate}
            onSessionStartTimeChange={setSessionStartTime}
            onSessionEndTimeChange={setSessionEndTime}
            onSessionInstructorChange={setSessionInstructor}
            onSessionDeliveryMethodChange={setSessionDeliveryMethod}
            onSessionLocationRoomChange={setSessionLocationRoom}
            onSessionVirtualLinkChange={setSessionVirtualLink}
            onAddSession={handleAddSession}
            onDeleteSession={handleDeleteSession}
            validationErrors={validationErrors}
          />
        );

      case 'sections':
        return (
          <SectionsLessonsTab
            courseId={createdCourseId}
            onUpdate={(sections) => {
              // Optional callback when sections are updated
              console.log('Sections updated:', sections);
            }}
          />
        );

      case 'offerings':
        return <OfferingsSessionsTab courseId={createdCourseId} />;

      case 'settings':
        return (
          <SettingsTab
            visibilityType={visibilityType}
            onVisibilityTypeChange={setVisibilityType}
            requiredModules={requiredModules}
            minimumScore={minimumScore}
            sequentialLearning={sequentialLearning}
            managerApproval={managerApproval}
            onRequiredModulesChange={setRequiredModules}
            onMinimumScoreChange={setMinimumScore}
            onSequentialLearningChange={setSequentialLearning}
            onManagerApprovalChange={setManagerApproval}
            enrollmentType={enrollmentType}
            dueDate={dueDate}
            sendReminders={sendReminders}
            onEnrollmentTypeChange={setEnrollmentType}
            onDueDateChange={setDueDate}
            onSendRemindersChange={setSendReminders}
            validationErrors={validationErrors}
          />
        );

      case 'review':
        return (
          <ReviewTab
            courseType={courseType}
            courseTitle={courseTitle}
            instructorName={instructorName}
            shortDescription={shortDescription}
            description={description}
            categoryName={getCategoryName()}
            subcategoryName={getSubcategoryName()}
            languageName={getLanguageName()}
            level={level}
            courseDuration={courseDuration}
            tags={tags}
            requirements={requirements}
            outcomes={outcomes}
            faqCount={faqs.filter(f => f.question.trim() && f.answer.trim()).length}
            thumbnailFile={thumbnailFile}
            modulesCount={selectedModules.length}
            offeringsCount={offerings.length}
            sessionsCount={sessions.length}
            visibilityType={visibilityType}
            isCreating={isCreating}
            onCreateCourse={handleCreateCourse}
            isUpdate={!!createdCourseId}
            validationErrors={validationErrors}
            error={error}
            success={success}
          />
        );

      default:
        return null;
    }
  };

  // Calculate progress
  const progress = (currentStep / tabs.length) * 100;

  return (
    <div className="course-creator">
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <div className="header-info">
            <h1>
              Create New Course
              {createdCourseId && (
                <span style={{
                  marginLeft: '15px',
                  fontSize: '14px',
                  fontWeight: 'normal',
                  color: '#FF8C00',
                  backgroundColor: '#FFF3E0',
                  padding: '4px 12px',
                  borderRadius: '4px'
                }}>
                  Draft (ID: {createdCourseId})
                </span>
              )}
            </h1>
            <p>Build and publish your educational content</p>
          </div>
          <div className="progress-info">
            <div className="step-indicator">
              Step {currentStep} of {tabs.length}
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="nav-tabs">
        <div className="nav-content">
          <div className="tabs-container">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const isCompleted = completedTabs.includes(tab.id);

              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setCurrentStep(tab.step);
                  }}
                  className={`tab-button ${isActive ? 'active' : ''} ${
                    isCompleted ? 'completed' : ''
                  }`}
                >
                  {isCompleted ? <Check size={20} /> : <Icon size={20} />}
                  <div className="tab-info">
                    <div className="tab-label">{tab.label}</div>
                    <div className="tab-description">{tab.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Global Error Messages */}
        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '12px 20px',
            borderRadius: '4px',
            marginBottom: '20px',
            border: '1px solid #f5c6cb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#721c24',
                cursor: 'pointer',
                fontSize: '20px',
                padding: '0 5px'
              }}
            >
              ×
            </button>
          </div>
        )}

        <div className="content-container">{renderTabContent()}</div>

        {/* Navigation Buttons */}
        {activeTab !== 'review' && (
          <div className="nav-buttons">
            <button
              className="btn-secondary"
              onClick={handlePrevious}
              disabled={currentStep === 1 || isCreating}
            >
              <ChevronLeft size={20} />
              Previous
            </button>

            <button
              className="btn-primary"
              onClick={handleNext}
              disabled={isCreating}
              style={{
                opacity: isCreating ? 0.7 : 1,
                cursor: isCreating ? 'not-allowed' : 'pointer'
              }}
            >
              {isCreating ? 'Saving...' : 'Next'}
              {!isCreating && <ChevronRight size={20} />}
            </button>
          </div>
        )}
      </div>

      {/* Add Modules Modal */}
      {showModulesModal && (
        <div className="trainer-coursemanagement-modal-overlay" onClick={() => setShowModulesModal(false)}>
          <div className="trainer-coursemanagement-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="trainer-coursemanagement-modal-header">
              <h2>Add Modules</h2>
              <button
                className="trainer-coursemanagement-icon-btn"
                onClick={() => setShowModulesModal(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="trainer-coursemanagement-modal-body">
              <div className="trainer-coursemanagement-modal-section">
                <h3>Select from Content Library</h3>
                <div className="trainer-coursemanagement-content-library-list">
                  {contentLibrary.map((item) => (
                    <div
                      key={item.id}
                      className={`trainer-coursemanagement-content-library-item ${
                        tempSelectedModules.includes(item.id) ? 'trainer-coursemanagement-selected' : ''
                      }`}
                      onClick={() => handleToggleModuleSelection(item.id)}
                    >
                      <div className="trainer-coursemanagement-content-library-info">
                        <p className="trainer-coursemanagement-content-library-title">{item.title}</p>
                        <p className="trainer-coursemanagement-content-library-meta">
                          {item.type} • {item.duration}
                        </p>
                      </div>
                      {tempSelectedModules.includes(item.id) && (
                        <div className="trainer-coursemanagement-content-library-check">
                          <Check size={20} color="#FF8C00" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="trainer-coursemanagement-modal-footer">
              <button
                className="trainer-coursemanagement-btn-secondary"
                onClick={() => setShowModulesModal(false)}
              >
                Cancel
              </button>
              <button
                className="trainer-coursemanagement-btn-primary"
                onClick={handleSaveModules}
              >
                Add Selected Modules
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="trainer-coursemanagement-modal-overlay" onClick={() => setShowAddCategoryModal(false)}>
          <div className="trainer-coursemanagement-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="trainer-coursemanagement-modal-header">
              <h2>Add New Category</h2>
              <button
                className="trainer-coursemanagement-icon-btn"
                onClick={() => setShowAddCategoryModal(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="trainer-coursemanagement-modal-body">
              <div className="trainer-coursemanagement-modal-section">
                <label htmlFor="newCategoryName" className="trainer-coursemanagement-modal-label">
                  Category Name
                </label>
                <input
                  id="newCategoryName"
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g., Programming, Business, Design"
                  className="trainer-coursemanagement-modal-input"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !addingCategory) {
                      handleAddCategory();
                    }
                  }}
                />
              </div>
            </div>

            <div className="trainer-coursemanagement-modal-footer">
              <button
                className="trainer-coursemanagement-btn-secondary"
                onClick={() => {
                  setShowAddCategoryModal(false);
                  setNewCategoryName('');
                }}
                disabled={addingCategory}
              >
                Cancel
              </button>
              <button
                className="trainer-coursemanagement-btn-primary"
                onClick={handleAddCategory}
                disabled={addingCategory || !newCategoryName.trim()}
              >
                {addingCategory ? 'Adding...' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Subcategory Modal */}
      {showAddSubcategoryModal && (
        <div className="trainer-coursemanagement-modal-overlay" onClick={() => setShowAddSubcategoryModal(false)}>
          <div className="trainer-coursemanagement-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="trainer-coursemanagement-modal-header">
              <h2>Add New Subcategory</h2>
              <button
                className="trainer-coursemanagement-icon-btn"
                onClick={() => setShowAddSubcategoryModal(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="trainer-coursemanagement-modal-body">
              {!category && (
                <div className="trainer-coursemanagement-modal-warning">
                  Please select a category first before adding a subcategory.
                </div>
              )}

              <div className="trainer-coursemanagement-modal-section">
                <label htmlFor="categoryForSubcategory" className="trainer-coursemanagement-modal-label">
                  Parent Category
                </label>
                <select
                  id="categoryForSubcategory"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="trainer-coursemanagement-modal-select"
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>

                <label htmlFor="newSubcategoryName" className="trainer-coursemanagement-modal-label">
                  Subcategory Name
                </label>
                <input
                  id="newSubcategoryName"
                  type="text"
                  value={newSubcategoryName}
                  onChange={(e) => setNewSubcategoryName(e.target.value)}
                  placeholder="e.g., Web Development, Data Science"
                  className="trainer-coursemanagement-modal-input"
                  disabled={!category}
                  autoFocus={!!category}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !addingSubcategory && category) {
                      handleAddSubcategory();
                    }
                  }}
                />
              </div>
            </div>

            <div className="trainer-coursemanagement-modal-footer">
              <button
                className="trainer-coursemanagement-btn-secondary"
                onClick={() => {
                  setShowAddSubcategoryModal(false);
                  setNewSubcategoryName('');
                }}
                disabled={addingSubcategory}
              >
                Cancel
              </button>
              <button
                className="trainer-coursemanagement-btn-primary"
                onClick={handleAddSubcategory}
                disabled={addingSubcategory || !category || !newSubcategoryName.trim()}
              >
                {addingSubcategory ? 'Adding...' : 'Add Subcategory'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      <MessageModal
        isOpen={showMessageModal}
        onClose={handleMessageModalClose}
        type={modalType}
        title={modalTitle}
        message={modalMessage}
      />

      {/* Loading Overlay */}
      {isCreating && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
        }}>
          <LoadingSpinner size="lg" />
        </div>
      )}
    </div>
  );
};

export default CreateCourseTab;
